import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    ModalBox,
    TabContentBox,
    PreformattedText
} from '@components/EmbedDocuments/DocumentsTab/DocumentsTab.styles'; // Reuse styles
import {
    Modal, Typography, Button, Box, Tabs, Tab, CircularProgress, Alert, Paper
} from '@mui/material';
import { Download, Close } from '@mui/icons-material';
import { documentEmbeddingApi, performDownload } from '@api/api';
import DocViewer, { DocViewerRenderers } from 'react-doc-viewer';

// Reusable TabPanel component
function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`view-doc-tabpanel-${index}`}
            aria-labelledby={`view-doc-tab-${index}`}
            style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflowY: 'auto' }}
            {...other}
        >
            {value === index && (
                <TabContentBox sx={{ flexGrow: 1 }}>
                    {children}
                </TabContentBox>
            )}
        </div>
    );
}

export default function FileViewerModal({ open, onClose, fileId, initialDocInfo = {} }) {
    const [viewTabValue, setViewTabValue] = useState(0);
    // Use initialDocInfo but allow updates if fetched data provides more info (like exact filename)
    const [docInfo, setDocInfo] = useState(initialDocInfo);
    const [parsedData, setParsedData] = useState({ loading: false, error: null, text: null, hash: null });
    const [chunkData, setChunkData] = useState({ loading: false, error: null, data: null });
    const [rawFileData, setRawFileData] = useState({
        loading: false, error: null, blobUrl: null, blob: null, fileType: null, filename: null
    });

    const currentBlobUrl = useRef(null);

    // --- Data Fetching ---
    const fetchParsedData = useCallback(async (id) => {
        if (!id) return null;
        setParsedData({ loading: true, error: null, text: null, hash: null });
        try {
            const result = await documentEmbeddingApi.getParsedDocument(id);
            if (!result.hash) { // Backend sends hash even if text is empty, absence implies error/not found
                throw new Error(result.message || 'Parsed document hash not found.');
            }
            setParsedData({ loading: false, error: null, text: result.text || '', hash: result.hash }); // Ensure text is at least ''
            return result.hash;
        } catch (err) {
            console.error(`FileViewerModal: Failed to fetch parsed data for ID ${id}:`, err);
            setParsedData({ loading: false, error: err.message || 'Failed to fetch parsed data', text: null, hash: null });
            return null;
        }
    }, []);

    const fetchChunkData = useCallback(async (documentHash) => {
        if (!documentHash) return;
        setChunkData({ loading: true, error: null, data: null });
        try {
            const result = await documentEmbeddingApi.getDocumentChunks(documentHash);
            if (Array.isArray(result)) {
                setChunkData({ loading: false, error: null, data: result });
            } else if (result && result.error) {
                throw new Error(result.error || 'Backend failed to retrieve chunks.');
            } else {
                console.error("FileViewerModal: Unexpected chunks response format:", result);
                throw new Error('Unexpected format received for chunk data.');
            }
        } catch (err) {
            console.error(`FileViewerModal: Failed to fetch chunk data for hash ${documentHash}:`, err);
            setChunkData({ loading: false, error: err.message || 'Failed to fetch chunk data', data: null });
        }
    }, []);

    const fetchRawFileData = useCallback(async (id) => {
        if (!id) return;
        setRawFileData(prev => ({ ...prev, loading: true, error: null }));

        // Clean up previous blob URL if any
        if (currentBlobUrl.current) {
            URL.revokeObjectURL(currentBlobUrl.current);
            currentBlobUrl.current = null;
        }
        setRawFileData(prev => ({ ...prev, blobUrl: null, blob: null, fileType: null, filename: null })); // Reset relevant state

        try {
            const { blob, filename: fetchedFilename } = await documentEmbeddingApi.downloadRawDocumentBlob(id);
            const blobUrl = URL.createObjectURL(blob);
            currentBlobUrl.current = blobUrl; // Store ref to new blob URL

            // Determine file type and name, prioritizing fetched data
            const determinedFilename = fetchedFilename || initialDocInfo?.name || `file_${id}`;
            const extension = determinedFilename.split('.').pop()?.toLowerCase();
            const determinedFileType = initialDocInfo?.file_type || extension || 'unknown';


            // Update local docInfo state if fetched filename is more specific
            setDocInfo(prev => ({ ...prev, name: determinedFilename, file_type: determinedFileType }));

            setRawFileData({
                loading: false,
                error: null,
                blobUrl: blobUrl,
                blob: blob,
                fileType: determinedFileType,
                filename: determinedFilename
            });

        } catch (err) {
            console.error(`FileViewerModal: Error fetching raw file data for ID ${id}:`, err);
            setRawFileData({ loading: false, error: err.message || 'Failed to fetch raw file data.', blobUrl: null, blob: null, fileType: null, filename: null });
            // Ensure cleanup even on error
            if (currentBlobUrl.current) {
                URL.revokeObjectURL(currentBlobUrl.current);
                currentBlobUrl.current = null;
            }
        }
    // initialDocInfo might change if the modal is reused for different docs without closing/opening
    // }, [initialDocInfo?.name, initialDocInfo?.file_type]);
    }, [initialDocInfo]); // Depend on the initialDocInfo object itself

    // --- Effects ---
    useEffect(() => {
        if (open && fileId) {
            // Reset state when modal opens for a new fileId or with new initialDocInfo
            setDocInfo(initialDocInfo);
            setViewTabValue(0); // Reset to first tab
            setParsedData({ loading: false, error: null, text: null, hash: null });
            setChunkData({ loading: false, error: null, data: null });
            setRawFileData({ loading: false, error: null, blobUrl: null, blob: null, fileType: null, filename: null });

             // Immediately fetch raw file data for the first tab
             fetchRawFileData(fileId);
        } else if (!open) {
            // Cleanup blob URL when modal closes
            if (currentBlobUrl.current) {
                URL.revokeObjectURL(currentBlobUrl.current);
                currentBlobUrl.current = null;
            }
        }
        // Want this effect to re-run if the fileId changes while the modal is open,
        // or if initialDocInfo changes (less likely, but possible).
        // fetchRawFileData is stable due to useCallback.
    }, [open, fileId, initialDocInfo, fetchRawFileData]);

    // --- Handlers ---
    const handleViewTabChange = useCallback((_event, newValue) => {
        setViewTabValue(newValue);
        if (!fileId) return;

        // Fetch data for tab if not already loaded/loading/errored
        if (newValue === 1 && !parsedData.text && !parsedData.hash && !parsedData.loading && !parsedData.error) {
            fetchParsedData(fileId);
        } else if (newValue === 2 && !chunkData.data && !chunkData.loading && !chunkData.error) {
            if (parsedData.hash) { // If hash already available, fetch chunks
                 fetchChunkData(parsedData.hash);
            } else if (!parsedData.loading && !parsedData.error) { // If no hash and not loading/errored, try fetching parsed first
                 fetchParsedData(fileId).then(hash => {
                     if (hash) {
                         fetchChunkData(hash);
                     } else {
                         console.warn("FileViewerModal: Cannot fetch chunks because fetching parsed data failed or returned no hash.");
                         // Optionally set an error in chunkData state here
                         setChunkData(prev => ({...prev, error: 'Failed to get document hash needed for chunks.'}))
                     }
                 });
            } else if (parsedData.error) {
                 // If parsedData errored previously, reflect that in chunk tab
                 setChunkData(prev => ({...prev, error: `Cannot fetch chunks: ${parsedData.error}`}))
            }
        }
    }, [fileId, parsedData.text, parsedData.hash, parsedData.loading, parsedData.error, chunkData.data, chunkData.loading, chunkData.error, fetchParsedData, fetchChunkData]);

    const handleDownloadParsedText = () => {
        if (parsedData.text && docInfo?.name) {
            const blob = new Blob([parsedData.text], { type: 'text/plain;charset=utf-8' });
            const filename = `${docInfo.name.split('.')[0] || `file_${fileId}`}_parsed.txt`;
            performDownload(blob, filename);
        } else {
            console.warn("FileViewerModal: Cannot download parsed text, data missing.", { textExists: !!parsedData.text, docInfo });
        }
    };

    const handleDownloadRawFile = () => {
        if (rawFileData.blob && rawFileData.filename) {
            performDownload(rawFileData.blob, rawFileData.filename);
        } else {
            console.warn("FileViewerModal: Cannot download raw file, data missing.", { blob: rawFileData.blob, filename: rawFileData.filename });
            // Optionally show an alert to the user
        }
    };

    // Custom close handler to ensure cleanup before calling parent onClose
    const handleClose = () => {
        console.log("FileViewerModal: handleClose called.");
        if (currentBlobUrl.current) {
            console.log("FileViewerModal: Revoking Blob URL on explicit close:", currentBlobUrl.current);
            URL.revokeObjectURL(currentBlobUrl.current);
            currentBlobUrl.current = null;
        }
        onClose(); // Call the parent handler
    };

    // --- Render ---
    if (!open) return null;

    const displayName = docInfo?.name || (fileId ? `ID: ${fileId}` : 'Loading...');

    return (
        <Modal open={open} onClose={handleClose} aria-labelledby="file-viewer-modal-title">
            {/* Increased width and height */}
            <ModalBox sx={{ display: 'flex', flexDirection: 'column', height: '90vh', width: '85vw', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #eee', flexShrink: 0 }}>
                    <Typography variant="h6" id="file-viewer-modal-title">
                        View Document: {displayName}
                    </Typography>
                    <Button onClick={handleClose} sx={{ minWidth: 'auto' }}><Close /></Button>
                </Box>

                <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                    <Tabs value={viewTabValue} onChange={handleViewTabChange} aria-label="view document tabs">
                        <Tab label="Raw File" id="view-doc-tab-0" aria-controls="view-doc-tabpanel-0" />
                        <Tab label="Parsed Text" id="view-doc-tab-1" aria-controls="view-doc-tabpanel-1" />
                        <Tab label="Chunks" id="view-doc-tab-2" aria-controls="view-doc-tabpanel-2" />
                    </Tabs>
                </Box>

                {/* Container for TabPanel content that handles scrolling */}
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' /* Parent handles overflow */ }}>
                    {/* Raw File Tab */}
                    <TabPanel value={viewTabValue} index={0}>
                        {rawFileData.loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>}
                        {rawFileData.error && <Alert severity="error" sx={{ m: 2 }}>Raw File Error: {rawFileData.error}</Alert>}
                        {rawFileData.blobUrl && !rawFileData.error && (
                            <>
                                <Button
                                    variant="outlined"
                                    startIcon={<Download />}
                                    onClick={handleDownloadRawFile}
                                    // Disable if blob/filename not ready (e.g., during loading or after error)
                                    disabled={!rawFileData.blob || !rawFileData.filename}
                                    sx={{ m: 2, mb: 0, alignSelf: 'flex-start', flexShrink: 0 }}
                                >
                                    Download Raw File ({rawFileData.filename || '...'})
                                </Button>
                                <Box sx={{ flexGrow: 1, border: '1px solid #eee', m: 2, overflow: 'hidden' /* DocViewer handles internal scroll */ }}>
                                     {/* Add a check for supported types if desired, or let DocViewer handle it */}
                                    <DocViewer
                                        documents={[{ uri: rawFileData.blobUrl, fileType: rawFileData.fileType, fileName: rawFileData.filename }]}
                                        pluginRenderers={DocViewerRenderers}
                                        config={{ header: { disableHeader: true } }}
                                        style={{ height: '100%' }} // Fill the container
                                        // Optional: Add error handling for viewer itself
                                        // onError={(e) => console.error("DocViewer error:", e)}
                                    />
                                </Box>
                            </>
                        )}
                         {/* Informative message if preview might not work */}
                        {rawFileData.blobUrl && !rawFileData.loading && !rawFileData.error && !rawFileData.fileType?.match(/^(pdf|docx?|xlsx?|pptx?|txt|csv|png|jpe?g|gif|bmp|svg)$/i) && (
                            <Alert severity="info" sx={{ m: 2 }}>Preview may not be available for this file type ({rawFileData.fileType}), but you can download it.</Alert>
                        )}
                    </TabPanel>

                    {/* Parsed Text Tab */}
                    <TabPanel value={viewTabValue} index={1}>
                         {parsedData.loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>}
                         {parsedData.error && <Alert severity="error" sx={{ m: 2 }}>Parsed Text Error: {parsedData.error}</Alert>}
                         {/* Show content even if text is empty string, but not if null/undefined */}
                         {parsedData.text !== null && parsedData.text !== undefined && !parsedData.loading && !parsedData.error && (
                             <>
                                 <Button
                                     variant="outlined"
                                     startIcon={<Download />}
                                     onClick={handleDownloadParsedText}
                                     disabled={!parsedData.text} // Disable if text is empty or null
                                     sx={{ m: 2, mb: 0, alignSelf: 'flex-start', flexShrink: 0 }}
                                 >
                                     Download Parsed Text
                                 </Button>
                                 <PreformattedText sx={{ flexGrow: 1, overflowY: 'auto', m: 2, p: 1, border: '1px solid #eee' }}>
                                     {parsedData.text || <Typography sx={{ color: 'text.secondary' }}>(No parsed text content)</Typography>}
                                 </PreformattedText>
                             </>
                         )}
                         {/* Message if loading finished, no error, but text is null/undefined */}
                          {parsedData.text === null && !parsedData.loading && !parsedData.error && (
                            <Typography sx={{ p: 2, color: 'text.secondary' }}>No parsed text available.</Typography>
                         )}
                    </TabPanel>

                    {/* Chunks Tab */}
                    <TabPanel value={viewTabValue} index={2}>
                        {/* Show loading if chunk data is loading OR if parsed data is loading (as it's a prerequisite) */}
                        {(chunkData.loading || (parsedData.loading && !parsedData.hash && viewTabValue === 2)) && <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>}
                        {/* Show chunk error OR parsed error if it prevented chunk loading */}
                        {(chunkData.error || (parsedData.error && !parsedData.hash && viewTabValue === 2)) &&
                            <Alert severity="error" sx={{ m: 2 }}>
                                Chunk Data Error: {chunkData.error || `Could not retrieve document hash needed for chunks (${parsedData.error})`}
                            </Alert>
                        }
                         {/* Only show chunk data if no loading and no errors */}
                        {!chunkData.loading && !chunkData.error && !(parsedData.error && !parsedData.hash && viewTabValue === 2) && (
                            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
                                {Array.isArray(chunkData.data) && chunkData.data.length > 0 ? (
                                    chunkData.data.map((node, idx) => (
                                        <Paper key={node.id_ || idx} variant="outlined" sx={{ p: 2, m: 1 }}>
                                            <Typography variant="overline" display="block" gutterBottom>Node {idx + 1} (ID: {node.id_ || 'N/A'})</Typography>
                                            <Typography variant="caption" display="block" gutterBottom>Text:</Typography>
                                            <PreformattedText sx={{ maxHeight: '200px', overflowY: 'auto', border: '1px dashed #ccc', p: 1 }}>
                                                {node.text || <Typography variant="body2" sx={{ color: 'text.secondary' }}>(No text)</Typography>}
                                            </PreformattedText>
                                            {/* Add metadata display if needed */}
                                        </Paper>
                                    ))
                                ) : (
                                    // Condition: Array exists, is empty, not loading, no relevant errors
                                    Array.isArray(chunkData.data) && chunkData.data.length === 0 &&
                                    <Typography sx={{ p: 2, color: 'text.secondary' }}>No chunks found for this document.</Typography>
                                )}
                            </Box>
                        )}
                    </TabPanel>
                </Box>
            </ModalBox>
        </Modal>
    );
}
